// A20 W7a — the triage view's data layer.
//
// Row shapes mirror `supabase/migrations/33_support_tickets.sql` column-for-
// column, in the same idiom as lib/api/trust.ts (that file's header calls itself
// "the THIRD copy of the shape" — this is the same hand-kept-in-lockstep deal).
//
// ── EVERYTHING GOES THROUGH THE LICENCE BACKEND ─────────────────────────────
// There is no direct table read here and there is no storage URL here. The
// console holds an anon key, `support_tickets` is service-role-only RLS, and
// migration 33 states the rule for the bytes outright: `recording_path` is a
// storage object path that must NEVER reach a browser. So the recording arrives
// as bytes from our own origin, via the streaming endpoint, and the console
// never learns where they are actually stored.
//
// ── THESE READ ENDPOINTS ARE NOT LANDED YET ─────────────────────────────────
// backend/src/routes/bug-report.routes.ts currently mounts three POSTs and no
// GETs; the reads (list, detail, and GET /reports/:id/recording) are a
// concurrent slice. This file therefore codes against the paths and is strict
// about the response shape: an unrecognised body raises, so the page shows an
// error rather than an empty list. "No tickets" and "I could not read the
// answer" are different facts and must never render the same.

import { apiRequest, apiUrl, supabase, ApiError } from '@/lib/supabase';

export type TicketStatus = 'open' | 'triaged' | 'in_progress' | 'resolved' | 'closed';
export type TicketSeverity = 'blocker' | 'major' | 'minor' | 'question';
export type TicketType = 'bug' | 'question' | 'feature_request' | 'walkthrough';
export type ReportSource = 'browser' | 'bridge' | 'onprem_drain' | 'anonymous';
export type GithubSyncState = 'pending' | 'synced' | 'failed' | 'skipped';
export type SurfaceKey =
  | 'tprm7.admin'
  | 'tprm7.portal'
  | 'tprm7.trust'
  | 'license.console'
  | 'license.portal';

/**
 * The SERVER's verdict on the stored capture — migration 41 SECTION 11, written
 * at ingest by backend/src/services/capture-masking.verify.ts.
 *
 * `masked` on the row beside it is the REPORTER'S CLAIM (a checkbox). These are
 * two different facts and the console renders both, because the case where they
 * disagree is the one worth seeing.
 *
 * 'verified_masked'  every sampled text value carried rrweb's mask shape.
 * 'contradicted'     at least one sampled value still reads as prose.
 * 'inconclusive'     too little text to judge, or legible text under a parent
 *                    the checker could not identify.
 * 'unverifiable'     the bytes could not be turned into an rrweb event array.
 */
export type MaskingVerdict =
  | 'verified_masked'
  | 'contradicted'
  | 'inconclusive'
  | 'unverifiable';

/** The columns the queue needs. A superset is fine; a subset renders as "—". */
export interface SupportTicketRow {
  id: string;
  ticket_ref: string;
  source: ReportSource;
  surface_key: SurfaceKey;
  route: string | null;
  product_version: string | null;
  severity: TicketSeverity;
  ticket_type: TicketType;
  status: TicketStatus;
  title: string;
  /** THE CLAIM: the reporter's checkbox, copied through ingest. Never a measurement. */
  masked: boolean;
  /**
   * THE FACT, when there is one. OPTIONAL — and optional on purpose, not out of
   * laziness: the licence backend drops these columns from its select if
   * migration 41 SECTION 11 has not been applied to its database, so a server
   * mid-deploy legitimately answers without them. `undefined` and `null` both
   * mean "no verdict"; the components render that as unchecked rather than as
   * either answer.
   */
  masking_verdict?: MaskingVerdict | null;
  capture_available: boolean;
  capture_duration_ms: number | null;
  instance_id: string | null;
  external_org_id: string | null;
  reporter_user_id: string | null;
  reporter_email: string | null;
  reporter_role: string | null;
  github_repo: string | null;
  github_issue_number: number | null;
  github_issue_url: string | null;
  github_sync_state: GithubSyncState;
  github_sync_error: string | null;
  github_synced_at: string | null;
  created_at: string;
}

export interface ConsoleEntry {
  level?: string;
  payload?: string[];
  trace?: string[];
  at?: number;
}

export interface NetworkEntry {
  method?: string;
  url?: string;
  status?: number;
  durationMs?: number;
  at?: number;
  failed?: boolean;
}

export interface ClientFacts {
  userAgent?: string;
  viewport?: { width?: number; height?: number };
  locale?: string;
  timeZone?: string;
  prefersReducedMotion?: boolean;
  colorScheme?: 'light' | 'dark';
}

/**
 * One row of `support_ticket_events` (migration 41 SECTION 1).
 *
 * ONE table, TWO state machines, keyed by `kind` — so `from_status`/`to_status`
 * hold ticket statuses on a 'status' row and github_sync_state values on a
 * 'github_sync' row. The table's own column comment says so; rendering them with
 * one label map would print "Sync pending → Sync pending" as if it were a
 * lifecycle move, so the timeline branches on `kind` before it labels anything.
 */
export type TicketEventKind = 'status' | 'github_sync';

export interface TicketEvent {
  id: string;
  ticket_id: string;
  kind: TicketEventKind;
  /** NULL only on a hand-written correction that cannot name a prior state. */
  from_status: string | null;
  to_status: string;
  /** A STAFF or SYSTEM identity — 'system:sync', 'github:<login>', or an operator. */
  actor: string;
  /**
   * The operator's note. CUSTOMER PAYLOAD by migration 41's own classification
   * (it routinely quotes the report), cleared by redact_ticket_rows — so a
   * historical row legitimately comes back with this null and the timeline must
   * render that as a redaction, not as "no reason given".
   */
  reason: string | null;
  created_at: string;
}

export interface SupportTicketDetail extends SupportTicketRow {
  body: string;
  locale: string | null;
  verified: boolean;
  console_json: ConsoleEntry[] | null;
  network_json: NetworkEntry[] | null;
  client_json: ClientFacts | null;
  recording_bytes: number | null;
  updated_at: string | null;
  resolved_at: string | null;
  /**
   * What the verdict is WORTH. Same optionality as `masking_verdict`, and
   * carried alongside it deliberately: 'verified_masked' over 6 sampled values,
   * or over a walk a cap cut short, is not the same statement as one over 300
   * complete ones. A component that renders the verdict without these is
   * telling half a truth.
   */
  masking_checked_at?: string | null;
  masking_sampled?: number | null;
  masking_legible?: number | null;
  masking_truncated?: boolean | null;

  /**
   * THE STATE MACHINE, AS THE DATABASE DEFINES IT — migration 41 SECTION 3's
   * allowed_ticket_transitions(), read per request and never re-declared here.
   *
   * THE THREE VALUES ARE THREE DIFFERENT FACTS AND THE CONTROL TREATS THEM SO:
   *   ['triaged', …]  these moves are legal — offer exactly these
   *   []              this ticket is TERMINAL (closed) — offer none, say why
   *   null/undefined  the server could not ask (migration 41 not applied, or an
   *                   older build) — offer none, and say THAT instead, because
   *                   guessing here is how the buttons and the database drift
   *                   into disagreeing and the operator collects a 409.
   */
  allowed_transitions?: TicketStatus[] | null;

  // ── The GitHub issue's own lifecycle, mirrored by the resolution loop ─────
  // Migration 41 SECTION 6. Optional for the same reason as the masking
  // columns: the licence backend drops them when the migration has not been
  // applied to its database, so `undefined` means "this server cannot tell you",
  // never "no".
  /** 'open' | 'closed', as last READ BACK. null = never read, which is not 'open'. */
  github_issue_state?: string | null;
  github_closed_at?: string | null;
  /** WHY it closed: GitHub's state_reason, verbatim and uncontrolled by design. */
  github_resolution?: string | null;
  github_resolution_by?: string | null;
  /** When the loop last looked. Stale here means the sweep is not running. */
  github_state_synced_at?: string | null;
  github_attempts?: number | null;
  github_next_attempt_at?: string | null;
  github_last_attempt_at?: string | null;
  /** Non-null = an operator released this ticket for publication. */
  github_escalated_at?: string | null;
  github_escalated_by?: string | null;
}

/**
 * The result of PATCH /reports/:id — set_ticket_status() row for row.
 *
 * `changed: false` is a real outcome, not an error: re-asserting a status is a
 * documented no-op that writes no audit event. The console says so rather than
 * claiming a transition that did not happen.
 */
export interface TicketStatusChange {
  id: string;
  ticket_ref: string;
  status: TicketStatus;
  previous_status: TicketStatus;
  changed: boolean;
  resolved_at: string | null;
  /**
   * Migration 36's retention clock as its trigger just recomputed it. THE MOST
   * CONSEQUENTIAL SIDE EFFECT OF A TRIAGE CLICK — closing a ticket starts a
   * 30-day countdown on its recording — so it comes back in the response and is
   * shown, never inferred.
   */
  purge_after: string | null;
}

/** The result of POST /reports/:id/github-sync/rearm — rearm_github_sync() row. */
export interface GithubSyncRearm {
  id: string;
  ticket_ref: string;
  /** Where it came from: 'failed' is a retry, 'skipped'/'pending' may be a publication. */
  from_state: string;
  /**
   * TRUE means THIS call stamped github_escalated_at — i.e. this call is what
   * granted permission to publish. It is not "the row was skipped".
   */
  escalated: boolean;
}

// ── Reads ──────────────────────────────────────────────────────────────────

/**
 * Accept the list envelopes this codebase actually uses — a bare array
 * (/trust/saas-details), `{ data }` (/organizations), or a named collection
 * (/trust/leads → `{ leads }`) — and REFUSE anything else. Guessing would turn
 * a contract mismatch into a silent empty state, which is the exact failure
 * mode A18 spent a workstream on.
 */
function unwrapList(payload: unknown): SupportTicketRow[] {
  if (Array.isArray(payload)) return payload as SupportTicketRow[];
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    for (const key of ['data', 'tickets', 'reports'] as const) {
      if (Array.isArray(obj[key])) return obj[key] as SupportTicketRow[];
    }
  }
  throw new ApiError(
    'The license server returned an unrecognised shape for GET /reports. The triage read may not be deployed yet.',
    502,
  );
}

export async function listTickets(params?: {
  status?: TicketStatus | '';
  surface?: SurfaceKey | '';
  limit?: number;
}): Promise<SupportTicketRow[]> {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.surface) q.set('surface', params.surface);
  q.set('limit', String(params?.limit ?? 200));
  return unwrapList(await apiRequest<unknown>(`/reports?${q.toString()}`));
}

export async function getTicket(id: string): Promise<SupportTicketDetail> {
  const payload = await apiRequest<unknown>(`/reports/${encodeURIComponent(id)}`);
  // Same refusal as the list: a body without an id is not a ticket.
  const row = (payload && typeof payload === 'object' && 'data' in (payload as object)
    ? (payload as { data: unknown }).data
    : payload) as SupportTicketDetail | null;
  if (!row || typeof row !== 'object' || typeof row.id !== 'string') {
    throw new ApiError(
      'The license server returned an unrecognised shape for GET /reports/:id. The triage read may not be deployed yet.',
      502,
    );
  }
  return row;
}

/**
 * One ticket's audit trail, newest first.
 *
 * Its own request, and its own failure. The timeline is the one surface that can
 * be absent on a perfectly healthy server — a licence backend running ahead of
 * its database has no `support_ticket_events` table and answers 501 — and the
 * report must still render underneath. Folding this into getTicket() would make
 * a missing audit table take the whole page down with it.
 */
export async function listTicketEvents(id: string): Promise<TicketEvent[]> {
  const payload = await apiRequest<unknown>(`/reports/${encodeURIComponent(id)}/events`);
  const rows =
    payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)
      ? ((payload as { data: unknown[] }).data as TicketEvent[])
      : Array.isArray(payload)
        ? (payload as TicketEvent[])
        : null;
  // Same refusal as the queue read, for the same reason: an unparseable body
  // must NOT render as "nothing has happened to this ticket". That sentence is
  // load-bearing — it is what an operator reads before deciding nobody has
  // touched a report.
  if (!rows) {
    throw new ApiError(
      'The license server returned an unrecognised shape for GET /reports/:id/events. The timeline read may not be deployed yet.',
      502,
    );
  }
  return rows;
}

// ── Mutations ──────────────────────────────────────────────────────────────
//
// Until A20 phase 1b this module exported three reads and NOTHING that could
// change a ticket, which is why migration 41's whole lifecycle — the transition
// check, the audit trail, the escalation — was reachable only by hand-crafting
// an authenticated request. These two functions are the console's half of it.
//
// Both PRESERVE THE SERVER'S MESSAGE. set_ticket_status() and
// rearm_github_sync() raise refusals written for a human ("from closed the legal
// moves are none — this state is terminal", "% is already synced; re-filing
// would create a duplicate issue") and the backend passes those through
// verbatim. apiRequest already surfaces `error.message` on an ApiError, so the
// callers must render `err.message` and must not replace it with a generic
// line — a triager told WHY can act; one told "409" cannot.

/** Unwrap `{ data }`, refusing anything else — see unwrapList's argument. */
function unwrapOne<T>(payload: unknown, endpoint: string): T {
  const row =
    payload && typeof payload === 'object' && 'data' in (payload as object)
      ? (payload as { data: unknown }).data
      : payload;
  if (!row || typeof row !== 'object') {
    throw new ApiError(`The license server returned an unrecognised shape for ${endpoint}.`, 502);
  }
  return row as T;
}

/**
 * Move one ticket's status.
 *
 * `reason` is optional at this layer because the RPC treats it as optional; the
 * CONTROL decides when to insist (see TicketActions). It is stored in
 * support_ticket_events.reason, which migration 41 classes as customer payload —
 * so it is a note about the ticket, not a place to paste the ticket.
 */
export async function setTicketStatus(
  id: string,
  status: TicketStatus,
  reason?: string,
): Promise<TicketStatusChange> {
  const payload = await apiRequest<unknown>(`/reports/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    // No `actor` field, deliberately: the server reads it from the verified
    // session. An audit row a client can self-attribute is not an audit row.
    body: JSON.stringify(reason ? { status, reason } : { status }),
  });
  return unwrapOne<TicketStatusChange>(payload, 'PATCH /reports/:id');
}

/**
 * Put one ticket's GitHub sync back in the queue.
 *
 * ⚠ FOR A `license.portal` TICKET THIS IS A PUBLICATION DECISION, not a retry.
 * The RPC stamps `github_escalated_at`, which is the ONLY thing fileTicket()
 * accepts as permission to override its standing refusal to auto-file customer
 * correspondence — so this call converts a ticket from "will be skipped" to
 * "will be published to GitHub", permanently. The caller must make that legible
 * before it is clicked; it is not a quiet button.
 */
export async function rearmGithubSync(id: string, reason?: string): Promise<GithubSyncRearm> {
  const payload = await apiRequest<unknown>(
    `/reports/${encodeURIComponent(id)}/github-sync/rearm`,
    { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) },
  );
  return unwrapOne<GithubSyncRearm>(payload, 'POST /reports/:id/github-sync/rearm');
}

// ── The recording ──────────────────────────────────────────────────────────

/** gzip magic. The wire format is whatever the browser gzip'd at capture time. */
function isGzip(bytes: Uint8Array): boolean {
  return bytes.length > 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
}

/**
 * Fetch and rehydrate an rrweb recording from one of OUR byte endpoints.
 *
 * `path` is an api-relative path ('/reports/:id/recording',
 * '/help-assets/:id/recording') and is the ONLY thing that varies: bug captures
 * and studio walkthroughs are the same wire format written by the same recorder
 * and served by two endpoints that hold the same masking rule, so they are
 * inflated by one function rather than by two that could drift. Nothing in the
 * URL a caller passes may come from a server response — the storage location is
 * masked precisely because the browser never learns a path.
 *
 * Deliberately a raw `fetch` rather than `apiRequest`: this response is bytes,
 * not JSON, and apiRequest ends in `response.json()`. The Authorization header
 * and the ApiError semantics (status 0 = transport failure) are reproduced so
 * the caller can classify failures exactly as it does everywhere else.
 *
 * The endpoint may hand us either raw gzip (Content-Encoding not set, because
 * the bytes were compressed by the reporter rather than by the transport) or
 * already-inflated JSON if a proxy unwrapped it. Both are handled by sniffing
 * the magic rather than trusting a header we do not control end to end.
 */
export async function fetchReplayEvents(path: string): Promise<unknown[]> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  let response: Response;
  try {
    response = await fetch(`${apiUrl}/api/v1${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (err) {
    throw new ApiError(err instanceof Error ? err.message : 'Network request failed', 0);
  }

  if (!response.ok) {
    let message = `Request failed (HTTP ${response.status})`;
    try {
      const j = (await response.json()) as { error?: { message?: string } };
      message = j.error?.message || message;
    } catch {
      /* a byte endpoint need not return JSON on failure */
    }
    throw new ApiError(message, response.status);
  }

  const raw = new Uint8Array(await response.arrayBuffer());
  let json: string;
  if (isGzip(raw)) {
    const DS = (globalThis as { DecompressionStream?: typeof DecompressionStream }).DecompressionStream;
    if (!DS) {
      throw new ApiError(
        'This browser cannot inflate the recording (DecompressionStream is unavailable).',
        501,
      );
    }
    const stream = new Blob([raw as BlobPart]).stream().pipeThrough(new DS('gzip'));
    json = await new Response(stream).text();
  } else {
    json = new TextDecoder().decode(raw);
  }

  const events: unknown = JSON.parse(json);
  if (!Array.isArray(events)) {
    throw new ApiError('The recording did not decode to an rrweb event array.', 502);
  }
  return events;
}

/** One bug report's session capture. */
export async function fetchRecordingEvents(id: string): Promise<unknown[]> {
  return fetchReplayEvents(`/reports/${encodeURIComponent(id)}/recording`);
}

// ── Display helpers ────────────────────────────────────────────────────────

export const SURFACE_LABEL: Record<SurfaceKey, string> = {
  'tprm7.admin': 'TPRM7 Admin',
  'tprm7.portal': 'TPRM7 Portal',
  'tprm7.trust': 'Trust Center',
  'license.console': 'Licence Console',
  'license.portal': 'Customer Portal',
};

export function surfaceLabel(key: string): string {
  return SURFACE_LABEL[key as SurfaceKey] ?? key;
}

/**
 * The verdict in an operator's words.
 *
 * Each one says WHO decided and HOW SURE, because the alternative — 'Masked' /
 * 'Not masked' — is exactly the unearned certainty this column was added to
 * replace. 'Server-checked' is not 'safe': see MaskBanner in chips.tsx.
 */
export const MASKING_VERDICT_LABEL: Record<MaskingVerdict, string> = {
  verified_masked: 'Server-checked — masked',
  contradicted: 'Server-checked — legible text found',
  inconclusive: 'Server-checked — could not judge',
  unverifiable: 'Server could not read the capture',
};

export const SOURCE_LABEL: Record<ReportSource, string> = {
  browser: 'Console session',
  bridge: 'Hosted bridge',
  onprem_drain: 'On-prem drain',
  anonymous: 'Anonymous (auth page)',
};

export function sourceLabel(key: string): string {
  return SOURCE_LABEL[key as ReportSource] ?? key;
}

export function severityTint(severity: string): { bg: string; fg: string } {
  switch (severity) {
    case 'blocker': return { bg: '#FEE2E2', fg: '#DC2626' };
    case 'major': return { bg: '#FFEDD5', fg: '#C2410C' };
    case 'minor': return { bg: '#FEF9C3', fg: '#A16207' };
    default: return { bg: '#E0E7FF', fg: '#4338CA' };
  }
}

/** ms → "42s" / "3m 05s". Capture windows are seconds-to-minutes, never hours. */
export function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return '—';
  const total = Math.round(ms / 1000);
  if (total < 60) return `${total}s`;
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ════════════════════════════════════════════════════════════════════════════
// A20 C5 — "is anyone collecting this install's logs?"
// ────────────────────────────────────────────────────────────────────────────
// The ticket→job half of migration 42's link. ITS OWN REQUEST, for exactly the
// reason listTicketEvents is: it spans a second table on a migration this
// database may not have, and a console that cannot answer this must still
// render the report underneath.
//
// ⚠ WHAT COMES BACK IS A REFERENCE, NEVER CONTENT. A support bundle is logs off
// a customer's own host and it stays there; the job's `result` carries at most a
// path and a size, and the server strips content-shaped keys on the way in AND
// on the way out. There is no download here because there is nothing to
// download — an engineer who needs the bundle asks the customer for it through
// a channel a human is on both ends of.
// ════════════════════════════════════════════════════════════════════════════

/** Mirrors migration 42's `support_tickets.support_bundle_state` CHECK. */
export type SupportBundleState =
  | 'enqueued'
  | 'observed'
  | 'disabled'
  | 'no_install'
  | 'unavailable';

export interface SupportBundleJob {
  id: string;
  instance_id: string;
  op_key: string;
  status: 'pending' | 'claimed' | 'done' | 'failed';
  params: Record<string, unknown>;
  /** Path + size ONLY. See the banner above. */
  result: Record<string, unknown>;
  enqueued_at: string;
  claimed_at: string | null;
  reported_at: string | null;
  support_ticket_id: string | null;
}

/**
 * Five outcomes, and they are five because they are five different facts.
 * Collapsing any pair into "nothing to show" is how a console starts implying
 * that nobody looked when in fact nobody could.
 */
export type SupportBundleView =
  | { kind: 'absent' }
  | { kind: 'no-link'; state: SupportBundleState }
  | { kind: 'job-gone'; state: SupportBundleState }
  | { kind: 'job'; state: SupportBundleState; job: SupportBundleJob }
  | { kind: 'unmigrated' };

export async function getSupportBundle(id: string): Promise<SupportBundleView> {
  const payload = await apiRequest<unknown>(
    `/reports/${encodeURIComponent(id)}/support-bundle`,
  );
  const row = (payload && typeof payload === 'object' && 'data' in (payload as object)
    ? (payload as { data: unknown }).data
    : payload) as SupportBundleView | null;
  // Strict about the shape, like every other read here: an unrecognised body is
  // an error the operator can see, never a silent "nothing".
  if (!row || typeof row !== 'object' || typeof (row as { kind?: unknown }).kind !== 'string') {
    throw new ApiError(
      'The license server returned an unrecognised shape for GET /reports/:id/support-bundle.',
      502,
    );
  }
  return row;
}

/**
 * The one line a triager needs when there is no job, per state.
 *
 * Written as statements of fact about THIS server's configuration, not as
 * apologies: `no_install` in particular is a finding worth acting on, and
 * `observed` names the switch so the reader knows the remedy is one env var.
 */
export const SUPPORT_BUNDLE_REASON: Record<SupportBundleState, string> = {
  enqueued: 'A support bundle has been requested from this install.',
  observed:
    'Automatic bundle collection is in observe mode on this server, so nothing was dispatched. Set SUPPORT_BUNDLE_AUTO_MODE=enforce to arm it.',
  disabled: 'Automatic bundle collection is switched off on this server.',
  no_install:
    'No active install matches the instance id on this report — it has never phoned home, its licence was deactivated, or the id belongs to another deployment.',
  unavailable:
    'The collect-support-bundle operation is missing or disabled in the operations catalog, so nothing could be requested.',
};

/** The bundle REFERENCE, pulled out of an ops result. Never any content. */
export function bundleReference(
  result: Record<string, unknown> | null | undefined,
): { path: string | null; bytes: number | null } {
  const nested = (result?.result && typeof result.result === 'object'
    ? (result.result as Record<string, unknown>)
    : result ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v : null);
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  return {
    path: str(nested.bundle_path) ?? str(nested.path) ?? str(nested.file),
    bytes:
      num(nested.bytes) ??
      (num(nested.size_mb) != null ? (num(nested.size_mb) as number) * 1024 * 1024 : null),
  };
}
